/**
 * A student's own report history, inside their profile.
 *
 * Same data and actions as the studio-wide Report History page
 * (`ReportHistory.tsx`), scoped to one student: no student-name link (this
 * tab is already inside that student's profile) and no search/month filters
 * (there is nothing left to filter once a student is already chosen).
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateLong, formatPeriod } from '@pp/shared';
import { ApiError, api } from '../api/client.js';
import { messageFor } from '../api/error-messages.js';
import { AdminReauthDialog } from '../components/ui/AdminReauthDialog.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { useApiData } from '../hooks/useApiData.js';
import { useSession } from '../session/SessionContext.js';
import './ReportHistory.css';

interface ReportVersion {
  id: string;
  reportId: string;
  versionNumber: number;
  status: 'rendering' | 'ready' | 'failed';
  failureReason: string | null;
  pdfUrl: string | null;
  generatedAt: string;
  periodStart: string;
  periodEnd: string;
  instructorName: string | null;
}

interface ReportListResponse {
  items: ReportVersion[];
  pagination: { limit: number; offset: number; total: number };
}

export function StudentReportsTab({ studentId }: { studentId: string }) {
  const { session, hasAdminAccess } = useSession();
  const isOwner = session?.role === 'owner';

  const [pendingDelete, setPendingDelete] = useState<ReportVersion | null>(null);
  const [reauthFor, setReauthFor] = useState<(() => void) | null>(null);
  const [actionError, setActionError] = useState<string>();

  const { data, error, loading, reload } = useApiData<ReportListResponse>(
    `/api/reports?studentId=${studentId}&limit=100`,
  );

  const items = data?.items ?? [];

  async function destroy(version: ReportVersion) {
    setActionError(undefined);
    try {
      await api.delete(`/api/reports/${version.reportId}/permanent`);
      reload();
    } catch (err) {
      setActionError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    }
  }

  /** Runs now if the admin window is open, otherwise buys ten minutes first. */
  function withAdmin(action: () => void) {
    if (hasAdminAccess()) action();
    else setReauthFor(() => action);
  }

  return (
    <div className="history">
      {error && <p role="alert">{error}</p>}
      {actionError && <p role="alert">{actionError}</p>}
      {loading && <p className="profile__muted">Yükleniyor…</p>}

      {!loading && items.length === 0 && (
        <div className="profile__empty">
          <p>Rapor bulunamadı.</p>
          <Link to={`/rapor-olustur?ogrenci=${studentId}`} className="history__cta">
            Rapor Oluştur
          </Link>
        </div>
      )}

      {items.length > 0 && (
        <ul className="history__list">
          {items.map((version) => (
            <li key={version.id} className="history__card">
              <div className="history__identity">
                <span className="history__period">
                  {formatPeriod(version.periodStart, version.periodEnd)}
                </span>
                <span className="history__meta">
                  {formatDateLong(version.generatedAt.slice(0, 10))}
                  {version.instructorName ? ` · ${version.instructorName}` : ''}
                  {/* A first version needs no number; a second one does. */}
                  {version.versionNumber > 1 ? ` · Sürüm ${version.versionNumber}` : ''}
                </span>
              </div>

              <div className="history__actions">
                {version.status === 'ready' && version.pdfUrl && (
                  <a
                    className="history__open"
                    href={version.pdfUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    PDF'i Aç
                  </a>
                )}

                {/* A generation that failed is shown rather than hidden: the
                    studio needs to know that report never went anywhere. */}
                {version.status !== 'ready' && (
                  <span className="history__failed">
                    {version.status === 'rendering'
                      ? 'Oluşturuluyor…'
                      : 'Oluşturulamadı — yeniden deneyin'}
                  </span>
                )}

                {isOwner && (
                  <button
                    type="button"
                    className="history__delete"
                    onClick={() => setPendingDelete(version)}
                  >
                    Sil
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {pendingDelete && (
        <ConfirmDialog
          title="Raporu sil"
          body={`${formatPeriod(pendingDelete.periodStart, pendingDelete.periodEnd)} dönemine ait rapor, tüm sürümleriyle ve PDF dosyalarıyla birlikte kalıcı olarak silinir. Öğrencinin ölçüm, fotoğraf ve değerlendirme kayıtları etkilenmez.`}
          confirmLabel="Kalıcı Olarak Sil"
          destructive
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => {
            const version = pendingDelete;
            setPendingDelete(null);
            withAdmin(() => void destroy(version));
          }}
        />
      )}

      {reauthFor && (
        <AdminReauthDialog
          action="Raporu kalıcı olarak silmek"
          onCancel={() => setReauthFor(null)}
          onConfirmed={() => {
            const action = reauthFor;
            setReauthFor(null);
            action();
          }}
        />
      )}
    </div>
  );
}
